import { Suspense, lazy, memo, startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { changeAppLanguage, determineLang, getLangFromPath } from '@/utils/languageUtils';

import GlobalStyle from '@/utils/GlobalStyle';
import InviteNewsletterCookie from './components/cookies/InviteNewsletterCookie';
import ProductsPreloader from '@/components/ProductsPreloader';
import RccCookie from '@/components/cookies/RccCookie';
//const Routes = lazy(() => import("@/layouts/Routes"));
import Routes from '@/layouts/Routes';
//const ScrollToTop = lazy(() => import("@/utils/ScrollToTop"));
import ScrollToTop from '@/utils/ScrollToTop';
import ThemeProviderContainer from '@/utils/ThemeProviderContainer';
const Toaster = lazy(() => import('react-hot-toast').then(m => ({ default: m.Toaster })));
import { appDataContext } from '@/context';
//import GlobalStyle from "@/utils/GlobalStyle";
// const { themeProviderContainer } = lazy(() =>
//   import("@/utils/ThemeProviderContainer")
// );
import { getUserToken } from '@/api/createTokenProvider';
import { useLangStore } from '@/store/lang-store';
import { useLocation } from 'react-router-dom';

const MemoizedRoutes = memo(Routes);

// import CookiesComponent from "@/components/cookies/CookiesComponent";
const ModalLoginCentered = lazy(() => import('@/components/Modals/ModalLoginCentered'));
//import ModalLoginCentered from "@/components/Modals/ModalLoginCentered";
const ResetPasswordPopup = lazy(() => import('@/components/popups/ResetPasswordPopup'));
//import ModalResetCentered from "@/components/Modals/ModalResetCentered";
const AccountCreationPopup = lazy(() => import('@/components/popups/AccountCreationPopup'));
// import AccountCreationPopup from "@/components/popups/AccountCreationPopup";

// import Cookie5Days from "./utils/cookies";

// The App component is the root component of our application
const App = ({ ssrInitialState, ...props }) => {
  const location = useLocation();
  const [lang, setLang] = useState(ssrInitialState?.lang || props.lang || null);
  const [properties, setProperties] = useState(
    ssrInitialState?.properties || props.properties || null,
  );
  const [routeMap, setRouteMap] = useState(ssrInitialState?.routeMap || props.routeMap || null);
  const { langStore, setLangStore } = useLangStore();

  // Track langStore in a ref to check it inside effects without triggering them
  const langStoreRef = useRef(langStore);
  useEffect(() => {
    langStoreRef.current = langStore;
  }, [langStore]);

  const [, setUserValue] = useState(null); // Init to null for SSR mismatch avoidance
  const [isAccountPopupVisible, setIsAccountPopupVisible] = useState(false);
  const [displayLogin, setDisplayLogin] = useState(false);
  const [displayReset, setDisplayReset] = useState(false);
  const [displayResetConditions, setDisplayResetConditions] = useState(null);
  const [urlToRedirect, setUrlToRedirect] = useState(null);

  // Initialize langStore from SSR if available, to prevent mismatch
  // We use a ref to track if it's the first mount with SSR data
  const isSSRHydrated = useRef(!!ssrInitialState);
  const ssrLangApplied = useRef(false);

  useEffect(() => {
    // Client-side user initialization.
    // We defer this slightly to avoid "Suspense boundary received an update before it finished hydrating"
    // which happens if an update occurs while React is recovering from a hydration mismatch (e.g. lazy routes).
    const timer = setTimeout(() => {
      if (typeof startTransition !== 'undefined') {
        startTransition(() => {
          setUserValue(getUserToken());
        });
      } else {
        setUserValue(getUserToken());
      }
    }, 1000); // Increased delay further to be absolutely sure hydration is done.
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        const detectedLang = determineLang();
        const currentLangStore = langStoreRef.current;

        // If we have SSR state and the detected language matches the SSR language,
        // we update the store but SKIP fetching the data again.
        if (isSSRHydrated.current && ssrInitialState?.langCode === detectedLang) {
          // Only update store if it differs to avoid unnecessary re-renders/transitions during hydration
          if (currentLangStore !== detectedLang) {
            startTransition(() => {
              setLangStore(detectedLang);
            });
          }
          isSSRHydrated.current = false; // Next time (e.g. navigation), behave normally
        } else {
          // Normal behavior: fetch data if needed
          if (detectedLang) {
            // Verify if we actually need to change language to avoid redundant fetches
            // Only fetch if data is not already present or if the language is different
            if (langStoreRef.current !== detectedLang) {
              startTransition(() => {
                changeAppLanguage(detectedLang, setLang, setProperties, setRouteMap);
                setLangStore(detectedLang);
              });
            }
          }
        }
      }, 500); // Increased delay to allow full hydration
      return () => clearTimeout(timer);
    }
  }, [setLangStore, ssrInitialState]);

  useEffect(() => {
    // This effect handles store updates (e.g. user switches language via menu)
    if (langStore) {
      // SSR already loaded the correct language data — skip the re-fetch ONCE on initial
      // hydration to prevent CLS. After that, always apply language changes so the user
      // can switch back to the original SSR language without being blocked.
      if (!ssrLangApplied.current && ssrInitialState?.lang && ssrInitialState?.langCode === langStore) {
        ssrLangApplied.current = true;
        return;
      }
      ssrLangApplied.current = true;
      startTransition(() => {
        changeAppLanguage(langStore, setLang, setProperties, setRouteMap);
      });
    }
  }, [langStore, setLang, setProperties, setRouteMap, ssrInitialState]);

  // Listen to URL changes to detect language changes from navigation
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlLang = getLangFromPath(location.pathname);

      // If fuelmatch changed the language, apply it to mazout
      const fuelmatchLang = sessionStorage.getItem('fuelmatch_lang_changed');
      if (fuelmatchLang) {
        sessionStorage.removeItem('fuelmatch_lang_changed');
        if (!urlLang || urlLang !== fuelmatchLang) {
          if (urlLang) {
            // URL has a language prefix but it doesn't match → redirect to correct prefix
            const targetPrefix = fuelmatchLang.startsWith('fr') ? '/belgique' : '/belgie';
            const currentPrefix = fuelmatchLang.startsWith('fr') ? '/belgie' : '/belgique';
            window.location.replace(location.pathname.replace(currentPrefix, targetPrefix));
          } else {
            // No language in URL (e.g. "/") → update store so RedirectBasedOnLanguage uses correct lang
            startTransition(() => {
              setLangStore(fuelmatchLang);
            });
          }
          return;
        }
      }

      if (urlLang && urlLang !== langStore) {
        startTransition(() => {
          setLangStore(urlLang);
        });
      }
    }
  }, [location.pathname, langStore, setLangStore]);

  const contextValue = useMemo(
    () => ({ routeMap, lang, properties }),
    [routeMap, lang, properties],
  );

  const openLoginPopup = (e) => {
    e?.detail?.redirect ? setUrlToRedirect(e.detail.redirect) : null;
    setDisplayLogin(true);
  };

  const closeAllPopup = () => {
    setDisplayReset(false);
    setDisplayLogin(false);
    setIsAccountPopupVisible(false);
    document.body.classList.remove('modal-open');
  };

  const openeResetPopup = (e) => {
    e?.detail ? setDisplayResetConditions(e.detail) : null;
    setDisplayReset(true);
  };

  const handleStorage = () => {
    const userToken = getUserToken();
    // startTransition: this can fire early (axios 401 interceptor dispatches
    // tokenUserChanged) while lazy routes are still hydrating (React #421)
    startTransition(() => {
      setUserValue(userToken);
    });
  };
  const toggleAccountPopup = () => setIsAccountPopupVisible((prev) => !prev);

  useEffect(() => {
    window.addEventListener('tokenUserChanged', handleStorage);
    window.addEventListener('ToogleAccountPopup', toggleAccountPopup);
    window.addEventListener('OpenLoginForm', openLoginPopup);
    window.addEventListener('CloseAnyForm', closeAllPopup);
    window.addEventListener('OpenResetPasswordForm', openeResetPopup);
    return () => {
      window.removeEventListener('OpenResetPasswordForm', openeResetPopup);
      window.removeEventListener('CloseAnyForm', closeAllPopup);
      window.removeEventListener('OpenLoginForm', openLoginPopup);
      window.removeEventListener('ToogleAccountPopup', toggleAccountPopup);
      window.removeEventListener('tokenUserChanged', handleStorage);
    };
  }, []);

  useEffect(() => {
    // Defer Google Analytics initialization until first user interaction
    // (no time-based fallback: keeps gtag.js out of the Lighthouse/PageSpeed audit)
    let gaInitialized = false;

    const handleInteraction = () => {
      gaInitialized = true;
      import('react-ga4').then(({ default: ReactGA }) => {
        ReactGA.initialize('G-713T51SF38');
      });

      // Cleanup listeners
      ['click', 'scroll', 'touchstart', 'mousemove'].forEach((event) =>
        window.removeEventListener(event, handleInteraction),
      );
    };

    // Last-chance hit: if the user leaves without ever interacting (bounce),
    // send a minimal page_view straight to the GA4 Measurement Protocol.
    // Lighthouse never triggers visibilitychange, so PageSpeed is unaffected.
    const sendBounceBeacon = () => {
      if (gaInitialized || document.visibilityState !== 'hidden') return;
      gaInitialized = true;

      let cid;
      try {
        cid = localStorage.getItem('ga_fallback_cid');
        if (!cid) {
          cid = `${Math.floor(Math.random() * 1e9)}.${Math.floor(Date.now() / 1000)}`;
          localStorage.setItem('ga_fallback_cid', cid);
        }
      } catch {
        cid = `${Math.floor(Math.random() * 1e9)}.${Math.floor(Date.now() / 1000)}`;
      }

      const params = new URLSearchParams({
        v: '2',
        tid: 'G-713T51SF38',
        cid,
        sid: String(Math.floor(Date.now() / 1000)),
        en: 'page_view',
        dl: window.location.href,
        dt: document.title,
        ul: navigator.language.toLowerCase(),
        sr: `${window.screen.width}x${window.screen.height}`,
      });
      navigator.sendBeacon(`https://www.google-analytics.com/g/collect?${params}`);
    };

    ['click', 'scroll', 'touchstart', 'mousemove'].forEach((event) =>
      window.addEventListener(event, handleInteraction, { passive: true, once: true }),
    );
    document.addEventListener('visibilitychange', sendBounceBeacon);

    return () => {
      ['click', 'scroll', 'touchstart', 'mousemove'].forEach((event) =>
        window.removeEventListener(event, handleInteraction),
      );
      document.removeEventListener('visibilitychange', sendBounceBeacon);
    };
  }, []);

  return (
    <ThemeProviderContainer>
      <GlobalStyle />
      <ScrollToTop />
      <appDataContext.Provider value={contextValue}>
          {isAccountPopupVisible && (
            <Suspense fallback={null}>
              <AccountCreationPopup setIsAccountPopupVisible={setIsAccountPopupVisible} />
            </Suspense>
          )}
          {displayLogin && (
            <Suspense fallback={null}>
              <ModalLoginCentered
                isModalLogin={true}
                setDisplayLogin={setDisplayLogin}
                displayLogin={displayLogin}
                redirectAterLogin={urlToRedirect}
                setRedirectAterLogin={setUrlToRedirect}
              />
            </Suspense>
          )}
          {displayReset && (
            <Suspense fallback={null}>
              <ResetPasswordPopup setDisplayReset={setDisplayReset} {...displayResetConditions} />
            </Suspense>
          )}

          <ProductsPreloader />

          {lang && <MemoizedRoutes />}

          <RccCookie />

          <InviteNewsletterCookie />

          <Suspense fallback={null}>
            <Toaster
              position='top-right'
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4caf50',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#f44336',
                    secondary: '#fff',
                  },
                },
              }}
              containerStyle={{
                top: 20,
              }}
            />
          </Suspense>
      </appDataContext.Provider>
    </ThemeProviderContainer>
  );
};

export default App;